import { useState, useMemo, useCallback, useEffect } from "react"
import {
  useGetIngestionHistoryByConnectorQuery,
  IngestionStatus,
} from "~/ui-rtk/api/ingestionApi"
import { CSVUploadForm } from "~/ui-rtk/components/features/ingestion"
import { Loader } from "~/ui-rtk/components/ui/common"
import { Button } from "~/ui-rtk/components/ui/controls"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { copyTextToClipboard } from "~/ui-rtk/utils/clipboard"
import dayjs from "dayjs"
import IngestionHistoryDetail from "~/ui-rtk/components/features/ingestion/IngestionHistoryDetail"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import {
  STATUS_COLORS,
  STATUS_LABELS,
  formatFileSize,
} from "~/ui-rtk/components/features/ingestion/utils"

type UploadedFilesSectionProps = {
  companyConnectorId: string
  metadata?: Record<string, unknown>
  ingestionConfig?: Record<string, unknown>
}

const StatusBadge: React.FC<{ status: IngestionStatus }> = ({ status }) => (
  <span
    className={cn(
      "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
      STATUS_COLORS[status],
    )}
  >
    {STATUS_LABELS[status]}
  </span>
)

export default function UploadedFilesSection({
  companyConnectorId,
  metadata,
  ingestionConfig,
}: UploadedFilesSectionProps) {
  const [page, setPage] = useState(0)
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(
    null,
  )
  const [showUploadForm, setShowUploadForm] = useState(false)
  const [copied, setCopied] = useState(false)
  const company = useCurrentCompany()
  const pageSize = 10

  // Report type selection (APP-344). Reports live in `ingestionConfig.reports`.
  // The selection drives which email address is shown, which validation rules
  // apply on upload, and which slice of history is displayed.
  const reportsMap = useMemo(
    () =>
      (ingestionConfig?.reports ?? {}) as Record<
        string,
        { displayName?: string; info?: string }
      >,
    [ingestionConfig?.reports],
  )
  const reportEntries = useMemo(() => Object.entries(reportsMap), [reportsMap])

  const [selectedReportType, setSelectedReportType] = useState<string>("")

  useEffect(() => {
    if (reportEntries.length === 0) {
      setSelectedReportType("")
      return
    }
    if (!reportEntries.some(([key]) => key === selectedReportType)) {
      setSelectedReportType(reportEntries[0][0])
    }
  }, [reportEntries, selectedReportType])

  const queryParams = useMemo(
    () => ({
      companyConnectorId,
      limit: pageSize,
      offset: page * pageSize,
      ...(selectedReportType ? { reportType: selectedReportType } : {}),
    }),
    [companyConnectorId, page, pageSize, selectedReportType],
  )

  const {
    data: histories,
    isLoading,
    isError,
  } = useGetIngestionHistoryByConnectorQuery(queryParams)

  const hasMore = histories && histories.length === pageSize
  const hasPrevious = page > 0

  const handleNext = () => {
    if (hasMore) {
      setPage(p => p + 1)
    }
  }

  const handlePrevious = () => {
    if (hasPrevious) {
      setPage(p => p - 1)
    }
  }

  const handleHistoryClick = (historyId: string) => {
    setSelectedHistoryId(historyId)
  }

  const handleCloseDetail = () => {
    setSelectedHistoryId(null)
  }

  const handleUploadSuccess = () => {
    setShowUploadForm(false)
  }

  const emailPattern = ingestionConfig?.emailConfig
    ? ((ingestionConfig.emailConfig as Record<string, unknown>)
        ?.jobTypePattern as string | undefined)
    : undefined

  const supportsEmail =
    ingestionConfig?.deliveryMethods &&
    (ingestionConfig.deliveryMethods as string[]).includes("hybrid") &&
    emailPattern

  const ingestEmail = useMemo(() => {
    if (
      !supportsEmail ||
      !company?.id ||
      !emailPattern ||
      !selectedReportType
    ) {
      return null
    }
    return emailPattern
      .replace("{report_type}", selectedReportType)
      .replace("{company_id}", company.id)
  }, [supportsEmail, emailPattern, company?.id, selectedReportType])

  const handleCopy = useCallback(() => {
    if (!ingestEmail) return
    void copyTextToClipboard(ingestEmail).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }, [ingestEmail])

  if (isError) {
    return (
      <article className="max-w-89 w-full mb-4">
        <div className="p-4 bg-system-error-500/10 border border-system-error-500 rounded-lg">
          <p className="text-sm text-system-error-400">
            Failed to load uploaded files. Please try again later.
          </p>
        </div>
      </article>
    )
  }

  // Extract stats from metadata
  const totalUploads = (metadata?.totalUploads as number) || 0
  const lastUploadDate = metadata?.lastUploadDate as string | undefined

  return (
    <>
      <article className="w-full mb-4 text-left">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-platinum">
            Data Ingestion
          </h3>
          <Button
            className="bg-system-brand-pink-600 text-white text-sm rounded-system-md"
            onClick={() => setShowUploadForm(v => !v)}
          >
            {showUploadForm ? "Cancel" : "Upload CSV"}
          </Button>
        </div>

        {reportEntries.length > 1 && (
          <div className="mb-3">
            <label className="block text-xs text-gray-400 font-medium mb-1.5">
              Report type
            </label>
            <select
              className="select select-bordered w-full bg-gray-700 text-white"
              value={selectedReportType}
              onChange={e => {
                setSelectedReportType(e.target.value)
                setPage(0)
              }}
            >
              {reportEntries.map(([key, value]) => (
                <option key={key} value={key}>
                  {value.displayName || key}
                </option>
              ))}
            </select>
          </div>
        )}

        {reportsMap[selectedReportType]?.info && (
          <div className="mb-3 p-3 bg-basic-blue/10 border border-basic-blue/30 rounded-lg">
            <p className="text-xs text-gray-400 font-medium mb-1">
              About this report
            </p>
            <p className="text-sm text-platinum whitespace-pre-line leading-5">
              {reportsMap[selectedReportType].info}
            </p>
          </div>
        )}

        {showUploadForm && selectedReportType && (
          <div className="mb-4 p-3 bg-gray-700/30 border border-gray-600 rounded-lg">
            <CSVUploadForm
              companyConnectorId={companyConnectorId}
              reportType={selectedReportType}
              onSuccess={handleUploadSuccess}
              onError={undefined}
            />
          </div>
        )}

        {ingestEmail && !showUploadForm && (
          <div className="mb-4 p-3 bg-gray-700/30 border border-gray-600 rounded-lg space-y-2">
            <p className="text-xs text-gray-400 font-medium">
              Or send CSV via email:
            </p>
            <div className="flex items-center gap-2">
              <span className="text-xs text-white font-mono break-all flex-1">
                {ingestEmail}
              </span>
              <button
                onClick={handleCopy}
                className="shrink-0 px-2 py-1 text-xs font-medium rounded bg-basic-blue text-white hover:bg-basic-blue/80 transition-colors"
              >
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
          </div>
        )}

        {/* Stats Summary */}
        {(totalUploads > 0 || lastUploadDate) && (
          <div className="mb-4 p-3 bg-gray-700/30 border border-gray-600 rounded-lg">
            <div className="flex items-center justify-between gap-4 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-gray-400">Total Uploads:</span>
                <span className="font-semibold text-white">{totalUploads}</span>
              </div>
              {lastUploadDate && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">Last Upload:</span>
                  <span className="font-medium text-white">
                    {dayjs(lastUploadDate).format("MMM D, YYYY h:mm A")}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader />
          </div>
        ) : !histories || histories.length === 0 ? (
          <div className="p-4 bg-gray-700/30 border border-gray-600 rounded-lg">
            <p className="text-sm text-gray-400 text-center">
              No files uploaded yet. Upload a CSV file to get started.
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-2 mb-4">
              {histories.map(history => (
                <div
                  key={history.id}
                  className="p-3 bg-gray-700/30 border border-gray-600 rounded-lg hover:bg-gray-700/50 cursor-pointer transition-colors"
                  onClick={() => handleHistoryClick(history.id)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-white truncate">
                          {history.fileName}
                        </p>
                        <StatusBadge status={history.status} />
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-400">
                        <span>{formatFileSize(history.fileSize)}</span>
                        <span>•</span>
                        <span>
                          {dayjs(history.createdAt).format(
                            "MMM D, YYYY h:mm A",
                          )}
                        </span>
                        {history.deliveryMethod && (
                          <>
                            <span>•</span>
                            <span className="capitalize">
                              {history.deliveryMethod}
                            </span>
                          </>
                        )}
                        {history.reportType && (
                          <>
                            <span>•</span>
                            <span
                              className="px-1.5 py-0.5 rounded bg-basic-blue/30 text-platinum"
                              title="Report type"
                            >
                              {history.reportType}
                            </span>
                          </>
                        )}
                      </div>
                      {(history.validationErrors &&
                        history.validationErrors.length > 0) ||
                      (history.processingErrors &&
                        history.processingErrors.length > 0) ? (
                        <div className="mt-2">
                          <span className="text-xs text-system-error-400">
                            {history.validationErrors?.length || 0} validation
                            error
                            {history.validationErrors?.length !== 1 ? "s" : ""}
                            {history.processingErrors &&
                            history.processingErrors.length > 0
                              ? `, ${history.processingErrors.length} processing error${
                                  history.processingErrors.length !== 1
                                    ? "s"
                                    : ""
                                }`
                              : ""}
                          </span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {(hasPrevious || hasMore) && (
              <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-600">
                <Button
                  variant={{ variant: "outlined" }}
                  onClick={handlePrevious}
                  disabled={!hasPrevious}
                  className="text-sm"
                >
                  Previous
                </Button>
                <span className="text-xs text-gray-400">
                  Showing {page * pageSize + 1}-
                  {page * pageSize + (histories?.length || 0)} of many
                </span>
                <Button
                  variant={{ variant: "outlined" }}
                  onClick={handleNext}
                  disabled={!hasMore}
                  className="text-sm"
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </article>

      {/* Detail Dialog */}
      {selectedHistoryId && (
        <Dialog
          isOpen={!!selectedHistoryId}
          handleClose={handleCloseDetail}
          width={800}
          classes={{
            container:
              "bg-system-blue-800 border-2 border-system-blue-700 rounded-2xl p-6 relative flex flex-col max-w-2xl w-full max-h-[90vh] overflow-hidden",
          }}
        >
          <button
            className="absolute right-4 top-4 p-2 flex items-center justify-center hover:bg-basic-blue/50 text-platinum hover:text-white transition-colors rounded-lg"
            onClick={handleCloseDetail}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M1 1L13 13M13 1L1 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <IngestionHistoryDetail historyId={selectedHistoryId} />
        </Dialog>
      )}
    </>
  )
}
